import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Sparkles, Paperclip, X, Image as ImageIcon, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface InputAreaProps {
  onGenerate: (thought: string, watermark: string, image?: { data: string; mimeType: string }) => void;
  isProcessing: boolean;
}

const InputArea: React.FC<InputAreaProps> = ({ onGenerate, isProcessing }) => {
  const { hasPurchased } = useAuth();
  const [thought, setThought] = useState('');
  // Default watermark logic: if purchased, empty (no watermark). If free, @logisketch.
  const [watermark, setWatermark] = useState('@logisketch');
  const [selectedImage, setSelectedImage] = useState<{ data: string; mimeType: string; preview: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update watermark defaults when purchase status changes
  useEffect(() => {
    if (hasPurchased) {
      setWatermark(''); // Clear defaults for paid users
    } else {
      setWatermark('@logisketch'); // Enforce for free users
    }
  }, [hasPurchased]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((thought.trim() || selectedImage) && !isProcessing) {
      onGenerate(thought, watermark, selectedImage ? { data: selectedImage.data, mimeType: selectedImage.mimeType } : undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Please select an image under 5MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        // Extract base64 data and mime type
        const matches = result.match(/^data:(.+);base64,(.+)$/);
        if (matches) {
          setSelectedImage({
            mimeType: matches[1],
            data: matches[2],
            preview: result
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <section className="w-full max-w-2xl mx-auto px-2">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative">
        <div className="relative group">
            <textarea
                className="w-full p-6 text-xl bg-white border-2 border-black rounded-lg focus:outline-none focus:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] transition-all duration-200 resize-none min-h-[160px] placeholder-gray-400 font-hand leading-relaxed pb-12"
                placeholder="Write your idea here (or upload a reference image)..."
                value={thought}
                onChange={(e) => setThought(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isProcessing}
            />
            
            {/* Image Preview - Looks like a taped photo */}
            {selectedImage && (
                <div className="absolute bottom-4 left-4 z-10 animate-in zoom-in duration-200">
                    <div className="relative">
                        <div className="w-16 h-16 md:w-20 md:h-20 border-2 border-black bg-white p-1 shadow-md rotate-[-3deg]">
                            <img src={selectedImage.preview} alt="Reference" className="w-full h-full object-cover" />
                        </div>
                        <button 
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 border border-black shadow-sm hover:scale-110 transition-transform"
                        >
                            <X size={12} />
                        </button>
                    </div>
                </div>
            )}

            {/* Toolbar (Upload button) */}
            <div className="absolute bottom-4 left-4 z-0">
                 {!selectedImage && (
                    <button
                        type="button"
                        onClick={triggerFileSelect}
                        disabled={isProcessing}
                        className="flex items-center gap-2 text-gray-400 hover:text-gray-800 transition-colors font-hand text-sm"
                        title="Attach reference image"
                    >
                        <Paperclip size={20} />
                        <span className="hidden sm:inline">Add reference</span>
                    </button>
                 )}
                 <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleFileSelect} 
                    accept="image/*" 
                    className="hidden" 
                 />
            </div>

            <div className="absolute bottom-4 right-4 text-xs text-gray-300 font-hand pointer-events-none">
                Ctrl + Enter
            </div>
        </div>

        {/* Watermark Options */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pl-1">
            <div className="relative w-full sm:w-auto flex-1 max-w-xs">
                <label className="block text-xs font-hand text-gray-500 mb-1 ml-1">Watermark text</label>
                <div className="relative">
                    <input 
                        type="text"
                        value={watermark}
                        onChange={(e) => setWatermark(e.target.value)}
                        disabled={!hasPurchased}
                        placeholder="No watermark"
                        className={`w-full p-2 border-2 border-black rounded font-hand text-sm ${!hasPurchased ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                    />
                    {!hasPurchased && (
                        <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    )}
                </div>
                {!hasPurchased && (
                    <p className="text-[10px] text-gray-400 font-hand mt-1 ml-1">
                        Purchase credits to remove or customize watermark.
                    </p>
                )}
            </div>

            <div className="w-full sm:w-auto flex justify-end mt-4 sm:mt-0">
                <button
                    type="submit"
                    disabled={(!thought.trim() && !selectedImage) || isProcessing}
                    className={`
                    flex items-center gap-2 px-8 py-3 rounded-lg font-bold font-hand text-lg border-2 border-black transition-all duration-200 w-full sm:w-auto justify-center
                    ${(!thought.trim() && !selectedImage) || isProcessing 
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed shadow-none' 
                        : 'bg-white text-black hover:bg-yellow-300 hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-0 active:shadow-none'}
                    `}
                >
                    {isProcessing ? (
                    <>
                        <Sparkles className="animate-spin" size={20} />
                        <span>Inking...</span>
                    </>
                    ) : (
                    <>
                        <span>Draw It</span>
                        <ArrowRight size={20} />
                    </>
                    )}
                </button>
            </div>
        </div>
        
      </form>
    </section>
  );
};

export default InputArea;